<script setup lang="ts">
import { useRouter } from 'vue-router';
import { AccountRouteName } from '@/js/account/app/router/route-names';
import BaseLoadState from '@/js/account/components/base/BaseLoadState.vue';
import DashboardQuickAction from '@/js/account/components/dashboard/DashboardQuickAction.vue';
import DashboardStatCard from '@/js/account/components/dashboard/DashboardStatCard.vue';
import { useAdminDashboardStats } from '@/js/account/entities/dashboard';
import { classNames } from '@/js/lib/bem';

const cn = classNames('profile-dashboard-admin');

const router = useRouter();
const { stats, loading, error } = useAdminDashboardStats();

function formatTotal(total: number): string {
  return total.toLocaleString('en-US').replace(/,/g, ' ');
}

function formatThisWeek(thisWeek: number): string {
  return `${thisWeek > 0 ? '+' : ''}${thisWeek} this week`;
}

async function goToDocumentCreate(): Promise<void> {
  await router.push({ name: AccountRouteName.AdminDocumentCreate });
}
</script>

<template>
  <div :class="cn()">
    <BaseLoadState
      :loading="loading"
      :error="error"
      loading-message="Loading statistics…"
    >
      <section v-if="stats" :class="cn('statistics')">
        <DashboardStatCard
          icon="plum"
          title="Total Policies"
          :value="formatTotal(stats.policies.total)"
          :label="formatThisWeek(stats.policies.thisWeek)"
        />
        <DashboardStatCard
          icon="person-success"
          title="Users"
          :value="formatTotal(stats.users.total)"
          :label="formatThisWeek(stats.users.thisWeek)"
        />
        <DashboardStatCard
          icon="download"
          title="Downloads"
          :value="formatTotal(stats.downloads.total)"
          :label="formatThisWeek(stats.downloads.thisWeek)"
        />
      </section>
    </BaseLoadState>
    <section :class="cn('quick-actions')">
      <h2 :class="cn('quick-actions-title')">
        Quick Actions
      </h2>
      <div :class="cn('quick-actions-list')">
        <DashboardQuickAction
          icon="add-square"
          title="Add new policy"
          description="Upload and categorize new policy documents"
          button-label="Upload"
          @action="goToDocumentCreate"
        />
      </div>
    </section>
  </div>
</template>

<style lang="scss" scoped>
.profile-dashboard-admin {
  display: flex;
  flex-direction: column;
  gap: 40px;

  &__statistics {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }

  &__quick-actions-title {
    font-size: 24px;
    font-weight: 400;
    line-height: 1.2;
    color: var(--color-text-primary);
    font-family: var(--font-family-serif);
    margin: 0 0 21px;
  }

  &__quick-actions-list {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
  }

  @media (max-width: 1246px) {
    &__statistics {
      grid-template-columns: 1fr;
    }

    &__quick-actions-list {
      grid-template-columns: 1fr;
    }
  }
}
</style>
